import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApolloInstances } from '../../api';
import { insertIf } from '../../utils';
import {
  Identifier,
  PrimaryCategories,
  SingleProductChange,
} from '../../types/types';
import { Product, ProductAttributesWrapper } from '../../types/product';

import GET_CATEGORY_ATTRIBUTES from './queries/get-epp-attributes';
import GET_CROSSES from './queries/get-crosses';
import GET_CROSSES_BY_SELLER from './queries/get-crosses-by-seller';
import GET_PRODUCT from './queries/get-product';
import GET_PRODUCT_ATTRIBUTES_IN_CATEGORY_BY_SEARCH from './queries/get-product-attributes-in-category-by-search';
import GET_PRODUCTS_IN_CATEGORY_BY_SEARCH from './queries/get-products-in-category-by-search';
import GET_STATUSES from './queries/get-statuses';
import GET_SUPPLIERS from './queries/get-suppliers';
import GET_SYS_SELLER from './queries/get-parent-companies';
import PRODUCT_EDIT from './mutations/product-edit';
import GET_PRIMARY_CATEGORIES from './queries/get-primary-categories';
import GET_APTITUDE_SUBCATEGORIES from './queries/get-aptitudeSubcategories';
import GET_PACKAGES_TYPES from './queries/get-packages-types';
import GET_DISTRIBUTOR_PRODUCT from './queries/get-distributor-product';
import GET_CROSSES_BY_DISTRIBUTOR from './queries/get-crosses-by-distributor';
import GET_CROSSES_DISTRIBUTOR from './queries/get-crosses-distributor';
import { PublicCategorizationSchemes } from '@aptitude/shared-vanilla';
import { Features } from '../../types/features';

interface ProductParams {
  productId: number;
  filterDate?: string;
  categorizationSchemeId?: number;
  categoryAttributeTypes?: CategoryAttributeTypes[];
  variant?: string;
}

interface GetOrganizationsResponse {
  id: number;
  name: string;
  parent: Identifier;
  children?: Identifier[];
}

export interface CategoryParams {
  categorizationSchemeId?: number;
  categoryId?: number;
  searchTerm?: string;
  variant?: string;
  CONSUME_PRODUCT_DATA_MD_CAT_964488?: boolean;
}

export enum CategoryAttributeTypes {
  EPP = 'EPP',
  PRODUCT = 'UNSPSC',
}

export interface CrossesParams {
  searchValue: string;
  categoryId: number;
  distributorId?: number;
  variant?: string;
}

/**
 * @fixme
 * Considering that we group APIs by endpoint, this should be part of catalog-graphql.service.ts.
 */
@Injectable({
  providedIn: 'root',
})
export class CategoriesGraphqlService {
  constructor(private apollo: Apollo) {}

  getProduct(
    {
      productId,
      filterDate,
      categorizationSchemeId,
      categoryAttributeTypes,
      variant,
    }: ProductParams,
    features?: Features,
  ) {
    return this.apollo
      .use(ApolloInstances.CATALOG)
      .query<{ purchasedProduct: Product }>({
        fetchPolicy: 'no-cache',
        variables: {
          productId,
          filterDate,
          categorizationSchemeId,
          categoryAttributeTypes,
          variant,
        },
        query: features?.CONSUME_PRODUCT_DATA_MD_CAT_964488
          ? GET_DISTRIBUTOR_PRODUCT
          : GET_PRODUCT,
      })
      .pipe(
        map(({ data }) => {
          if (features?.PSC_REBRANDING_PTHLA_1056838) {
            data.purchasedProduct.categories =
              data.purchasedProduct.categories.map((category) => ({
                ...category,
                parents: category.parents.map((parent) => ({
                  ...parent,
                  name:
                    parent.id === PublicCategorizationSchemes.CC
                      ? 'CC'
                      : parent.name,
                })),
              }));
          }
          return data.purchasedProduct;
        }),
      );
  }

  /**
   * Retrieves category attributes based on the provided parameters and optional attribute types.
   * This method queries the Apollo GraphQL client using the `CATALOG` instance and processes
   * the response to include modified attribute names if `useNewPscLabel` is set to true.
   *
   * @param categoryParams - The parameters for the category query, including categorization scheme ID,
   *                         category ID, and an optional search term.
   * @param categoryAttributeTypes - An optional array of category attribute types to filter the query.
   * @param useNewPscLabel - A boolean flag indicating whether to replace the PSC scheme label in attribute names.
   *                         Defaults to `false`.
   * @returns An observable that emits a mapped array of category attributes with optional PSC label replacement.
   */
  getCategoryAttributes(
    categoryParams: CategoryParams,
    categoryAttributeTypes?: CategoryAttributeTypes[],
    useNewPscLabel = false,
  ) {
    return this.apollo
      .use(ApolloInstances.CATALOG)
      .query<{
        productAttributeNamesByCategory: ProductAttributesWrapper[];
      }>({
        fetchPolicy: 'no-cache',
        variables: {
          schemeId: categoryParams.categorizationSchemeId,
          categoryId: categoryParams.categoryId,
          searchValue: categoryParams.searchTerm,
          categoryAttributeTypes,
        },
        query: GET_CATEGORY_ATTRIBUTES,
      })
      .pipe(
        map(({ data }) => {
          return data.productAttributeNamesByCategory.map((prodAttributes) => ({
            ...prodAttributes,
            attributes: prodAttributes.attributes.map((attribute) => ({
              ...attribute,
              name: useNewPscLabel
                ? this.replacePscScheme(attribute.name)
                : attribute.name,
            })),
          }));
        }),
      );
  }

  /**
   * Replaces occurrences of "PSC" in the given scheme name with "CC" or "cc",
   * depending on the case of the original match.
   *
   * - If "PSC" is in uppercase, it will be replaced with "CC".
   * - If "PSC" is in lowercase, it will be replaced with "cc".
   *
   * @param schemeName - The scheme name to process.
   * @returns The scheme name with "PSC" replaced by "CC" or "cc".
   */
  private replacePscScheme(schemeName: string): string {
    return schemeName.replace(/\bPSC\b/gi, (match) =>
      match === match.toUpperCase() ? 'CC' : 'cc',
    );
  }

  /** @fixme
   * 1. This is getting products not crosses.
   * 3. Can this query be merged with the one in {@link getProductsInCategoryBySearch}
   */
  getCrosses(searchValue: string, categoryId: number, sysSellerId?: number) {
    return this.apollo
      .use(ApolloInstances.CATALOG)
      .query<{ optimizedPurchasedProducts: Product[] }>({
        fetchPolicy: 'no-cache',
        variables: sysSellerId
          ? {
              searchValue: searchValue,
              categoryId: categoryId,
              sysSellerId: sysSellerId,
            }
          : { searchValue: searchValue, categoryId: categoryId },
        query: sysSellerId ? GET_CROSSES_BY_SELLER : GET_CROSSES,
      })
      .pipe(map(({ data }) => data.optimizedPurchasedProducts));
  }

  getCrossesByDistributor({
    searchValue,
    categoryId,
    distributorId,
    variant,
  }: CrossesParams) {
    return this.apollo
      .use(ApolloInstances.CATALOG)
      .query<{ optimizedPurchasedProducts: Product[] }>({
        fetchPolicy: 'no-cache',
        variables: distributorId
          ? {
              searchValue: searchValue,
              categoryId: categoryId,
              distributorId: distributorId,
              variant,
            }
          : { searchValue: searchValue, categoryId: categoryId, variant },
        query: distributorId
          ? GET_CROSSES_BY_DISTRIBUTOR
          : GET_CROSSES_DISTRIBUTOR,
      })
      .pipe(map(({ data }) => data.optimizedPurchasedProducts));
  }

  getProductsInCategoryBySearch(
    categoryParams: CategoryParams,
  ): Observable<Product[]> {
    return this.apollo
      .use(ApolloInstances.CATALOG)
      .query<{ optimizedPurchasedProducts: Product[] }>({
        // Disable caching - otherwise initial render can take several minutes
        fetchPolicy: 'no-cache',
        query: GET_PRODUCTS_IN_CATEGORY_BY_SEARCH(categoryParams),
      })
      .pipe(
        map(({ data }) =>
          categoryParams.CONSUME_PRODUCT_DATA_MD_CAT_964488
            ? data.optimizedPurchasedProducts.map((product) => {
                product.hierarchy = [product.id];
                if (product.baseProductId) {
                  product.hierarchy.unshift(product.baseProductId);
                }
                return product;
              })
            : data.optimizedPurchasedProducts,
        ),
        catchError((error) => of([])),
      );
  }

  getProductAttributesInCategoryBySearch(
    categoryParams: CategoryParams,
    categoryAttributeTypes: string[],
  ): Observable<Product[]> {
    return this.apollo
      .use(ApolloInstances.CATALOG)
      .query<{ optimizedPurchasedProducts: Product[] }>({
        // Disable caching - otherwise initial render can take several minutes
        fetchPolicy: 'no-cache',
        query: GET_PRODUCT_ATTRIBUTES_IN_CATEGORY_BY_SEARCH(
          categoryParams,
          categoryAttributeTypes,
        ),
      })
      .pipe(map(({ data }) => data.optimizedPurchasedProducts || []));
  }

  getStatuses(): Observable<string[]> {
    return this.apollo
      .use(ApolloInstances.PRODUCT)
      .query<{ statuses: string[] }>({
        fetchPolicy: 'no-cache',
        query: GET_STATUSES,
      })
      .pipe(
        map(({ data }) => {
          return data.statuses;
        }),
      );
  }

  getSuppliers(parentCompanyId: number): Observable<Identifier[]> {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .query<{
        organization: GetOrganizationsResponse;
      }>({
        fetchPolicy: 'no-cache',
        query: GET_SUPPLIERS,
        variables: {
          parentCompanyId,
        },
      })
      .pipe(
        map(({ data }) => [
          { id: data.organization.id, name: data.organization.name },
          ...(data.organization.children ?? []),
        ]),
      );
  }

  getParentCompanies(parentCompanyId: number): Observable<Identifier[]> {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .query<{
        organization: GetOrganizationsResponse;
      }>({
        fetchPolicy: 'no-cache',
        query: GET_SYS_SELLER,
        variables: {
          parentCompanyId,
        },
      })
      .pipe(
        map(({ data }) => [
          { id: data.organization.id, name: data.organization.name },
          ...insertIf(!!data.organization.parent, data.organization.parent),
          ...(data.organization.children ?? []),
        ]),
      );
  }

  /**
   * Retrieves the primary categories from the Apollo GraphQL service.
   *
   * @param useNewPscLabel - A boolean flag indicating whether to use the new PSC label.
   *                         If `true`, the categorization scheme name for certain categories
   *                         will be updated based on specific conditions.
   * @returns An `Observable` emitting an array of `PrimaryCategories`.
   *
   * The method performs a GraphQL query using the Apollo client to fetch primary categories.
   * If `useNewPscLabel` is `true`, the categorization scheme name for categories with
   * `PublicCategorizationSchemes.CC` will be set to `'CC'`. Otherwise, the original data
   * is returned as-is.
   */
  getPrimaryCategories(
    useNewPscLabel = false,
  ): Observable<PrimaryCategories[]> {
    return this.apollo
      .use(ApolloInstances.CATEGORY)
      .query<{
        primaryCategories: PrimaryCategories[];
      }>({
        fetchPolicy: 'no-cache',
        query: GET_PRIMARY_CATEGORIES,
      })
      .pipe(
        map(({ data }) => {
          if (useNewPscLabel) {
            return (
              data?.primaryCategories.map((category) => ({
                ...category,
                categorizationScheme: {
                  ...category.categorizationScheme,
                  name:
                    category.categorizationScheme.id ===
                    PublicCategorizationSchemes.CC
                      ? 'CC'
                      : category.categorizationScheme.name,
                },
              })) || []
            );
          }
          return data?.primaryCategories || [];
        }),
      );
  }

  getAptitudeSubcategories(
    categoryId: number,
  ): Observable<PrimaryCategories[]> {
    return this.apollo
      .use(ApolloInstances.CATEGORY)
      .query<{
        category: {
          subcategoryRelationships: { subcategory: PrimaryCategories }[];
        };
      }>({
        fetchPolicy: 'no-cache',
        query: GET_APTITUDE_SUBCATEGORIES,
        variables: {
          categoryId,
        },
      })
      .pipe(
        map(({ data }) => {
          if (!data || !data.category) {
            return [];
          }
          return data.category.subcategoryRelationships.map(
            (x) => x.subcategory,
          );
        }),
      );
  }

  getPackagesTypes(): Observable<string[]> {
    return this.apollo
      .use(ApolloInstances.PRODUCT)
      .query<{ packagesTypes: string[] }>({
        fetchPolicy: 'no-cache',
        query: GET_PACKAGES_TYPES,
      })
      .pipe(map((result) => result.data?.packagesTypes || []));
  }

  productEditRequest(
    productId: number,
    changes: SingleProductChange[],
  ): Observable<boolean> {
    return this.apollo
      .use(ApolloInstances.PRODUCT)
      .mutate<{ productEdit: { success: boolean } }>({
        mutation: PRODUCT_EDIT,
        variables: {
          productId,
          changes,
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(map((result) => result.data?.productEdit.success || false));
  }
}
